import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useAccountStore, useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { AdAccountDTO } from '../../../../../../api-models/src'
import { AdAccounts } from '../../../../services/user'
import {
	GDateRange,
	GSection,
	GTransition
} from '../../../../components/basic-blocks'
import { useSearchParams } from 'react-router-dom'
import { ReportMethod } from './types'
import { UpcomingReports } from './upcomming-reports/upcomming-reports'
import { RecentReports } from './recent-reports/recent-report'
import { AvailableReports } from './available-reports/available-reports'
import { DownloadsReports } from './downloads-reports/downloads-reports'
import { ScheduledReport } from './schdule-reports/scheduled-report'
import { GNewTabs } from '../../../../components/tabs/g-new-tabs'
import { Calendar as FiCalendar } from 'components/icons'
import moment from 'moment/moment'
import { useFilterPersistence } from '../../../../hooks/use-filter-presistence'

export const ReportsPage = () => {
	const [searchParams, setSearchParams] = useSearchParams()
	const [tab, setTab] = useState('available')
	const {
		asset,
		setAccountAdAccounts,
		demoMode,
		getReportRequests,
		getRecentReports,
		adAccounts
	} = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			setAccountAdAccounts: s.setAccountAdAccounts,
			demoMode: s.demoMode,
			getReportRequests: s.getReportRequests,
			getRecentReports: s.getRecentReports,
			adAccounts: s.adAccounts
		}))
	)
	const { account } = useAccountStore(
		useShallow((s) => ({
			account: s.account
		}))
	)

	const breadCampaigns =
		searchParams.get('breadCampaigns') || searchParams.get('breadCampaign')
	const campaignId =
		searchParams.get('campaign') ||
		(breadCampaigns ? breadCampaigns.split(',')[0] : null)

	const [dateRange, setDateRange] = useState({
		from: moment().subtract(30, 'days').startOf('day').toDate(),
		to: moment().endOf('day').toDate()
	})
	const hasInitializedDateRange = useRef(false)
	const isMountedRef = useRef(true)

	const handleDateRangeChange = (e: { from: Date; to: Date }) => {
		setDateRange({ from: e.from, to: e.to })
	}

	const [adAccount, setAdAccount] = useState<AdAccountDTO | null>(null)
	const hasInitializedFromURL = useRef(false)
	const lastSyncedAdAccountIdRef = useRef<string | null>(null)
	const lastProcessedUrlValueRef = useRef<string | null>(null)
	const isInitializingRef = useRef(false)

	useFilterPersistence()

	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
		}
	}, [])

	useEffect(() => {
		if (!isMountedRef.current) return

		const fromParam = searchParams.get('from')
		const toParam = searchParams.get('to')

		if (fromParam && toParam) {
			const newFrom = moment(Number(fromParam)).toDate()
			const newTo = moment(Number(toParam)).toDate()

			if (
				!dateRange.from ||
				!dateRange.to ||
				dateRange.from.getTime() !== newFrom.getTime() ||
				dateRange.to.getTime() !== newTo.getTime()
			) {
				if (isMountedRef.current) {
					setDateRange({
						from: newFrom,
						to: newTo
					})
				}
			}
		} else if (!hasInitializedDateRange.current) {
			if (isMountedRef.current) {
				setDateRange({
					from: moment().subtract(30, 'days').startOf('day').toDate(),
					to: moment().endOf('day').toDate()
				})
				hasInitializedDateRange.current = true
			}
		}

		if (fromParam && toParam) {
			hasInitializedDateRange.current = true
		}
	}, [searchParams])

	useEffect(() => {
		if (adAccounts.length === 0) {
			return
		}

		const currentUrlParams = new URLSearchParams(window.location.search)
		let preSelectedAccountId =
			currentUrlParams.get('adAccount') ||
			currentUrlParams.get('adAccounts')?.split(',')[0]

		if (!preSelectedAccountId) {
			try {
				const stored = sessionStorage.getItem('clickguard_persisted_filters')
				if (stored) {
					const persistedFilters = JSON.parse(stored)
					if (persistedFilters.breadcrumbs?.adAccounts) {
						const accountIds =
							persistedFilters.breadcrumbs.adAccounts.split(',')
						preSelectedAccountId = accountIds[0]
					}
				}
			} catch (error) {
				// Ignore sessionStorage errors
			}
		}

		const isFirstRun = !hasInitializedFromURL.current
		if (isFirstRun) {
			isInitializingRef.current = true
		}

		if (
			preSelectedAccountId &&
			lastProcessedUrlValueRef.current !== preSelectedAccountId
		) {
			const matchedAccount =
				adAccounts.find((acc) => acc.id === preSelectedAccountId) || null
			if (matchedAccount) {
				setAdAccount(matchedAccount)
				lastSyncedAdAccountIdRef.current = matchedAccount.id
				lastProcessedUrlValueRef.current = preSelectedAccountId
			}
		} else if (
			!preSelectedAccountId &&
			lastProcessedUrlValueRef.current !== null
		) {
			setAdAccount(null)
			lastSyncedAdAccountIdRef.current = null
			lastProcessedUrlValueRef.current = null
		}

		if (isFirstRun) {
			hasInitializedFromURL.current = true
		}
	}, [adAccounts, searchParams])
	useLayoutEffect(() => {
		if (isInitializingRef.current && hasInitializedFromURL.current) {
			isInitializingRef.current = false
		}
	}, [adAccount])

	// No URL write-back — breadcrumb owns the URL params

	useEffect(() => {
		const fetchData = async () => {
			if (account?.id) {
				const adAccounts = await AdAccounts.find(account?.id, {})
				setAccountAdAccounts(adAccounts)
			}
		}

		fetchData()
	}, [account])

	useEffect(() => {
		setTab(searchParams.get('tab') || 'available')
	}, [searchParams])

	useEffect(() => {
		if (account && asset) {
			getRecentReports(account.id, asset.id)
			getReportRequests(account.id, asset.id, ReportMethod.AUTOMATED)
		}
	}, [account, asset])

	const tabs = useMemo(() => {
		return [
			{
				label: 'Available Reports',
				key: 'available',
				to: () =>
					`/workspace/${account?.id}/asset/${asset?.id}/reports?tab=available`
			},
			{
				label: 'Downloads',
				key: 'downloads',
				to: () =>
					`/workspace/${account?.id}/asset/${asset?.id}/reports?tab=downloads`
			},
			{
				label: 'Scheduled Reports',
				key: 'scheduled',
				to: () =>
					`/workspace/${account?.id}/asset/${asset?.id}/reports?tab=scheduled`
			}
		]
	}, [account, asset])

	return (
		<div className="flex flex-col gap-3 w-full">
			<div className="w-full flex gap-3">
				<div className="w-1/2">
					<UpcomingReports />
				</div>
				<div className="w-full">
					<RecentReports />
				</div>
			</div>
			<GSection
				containerClassName="!mb-0"
				borderClass="border border-t-border-light rounded-xl"
			>
				<div className="flex items-center justify-between gap-3 mb-4">
					<GNewTabs current={tab} tabs={tabs} />
					<GDateRange
						onApply={(e) => handleDateRangeChange({ from: e.from, to: e.to })}
						align="right"
						demoMode={demoMode}
						calender={FiCalendar}
					/>
				</div>
				<GTransition show={tab === 'available'}>
					<p className="text-base text-t-checkbox-unactive py-4">
						Schedule reports or run on-demand reports to be generated
						immediately
					</p>
					<AvailableReports />
				</GTransition>
				<GTransition show={tab === 'downloads'}>
					<p className="text-base text-t-checkbox-unactive  py-4">
						History of all previously generated reports
					</p>
					<DownloadsReports
						adAccount={adAccount?.id || null}
						campaign_id={campaignId || null}
						dateRange={dateRange}
					/>
				</GTransition>
				<GTransition show={tab === 'scheduled'}>
					<p className="text-base text-t-checkbox-unactive py-4">
						Scheduled reports that are generated on a set interval
					</p>
					<ScheduledReport />
				</GTransition>
			</GSection>
		</div>
	)
}
